<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { accountId, accountScopedRoute } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const activeStep = ref(null);
const spotlightStyle = ref({});
const tooltipStyle = ref({});

const copy = {
  skip: 'Fechar',
  previous: 'Voltar',
  next: 'Próximo',
  finish: 'Concluir',
  progressLabel: 'Passo',
};

const storageKey = computed(
  () => `talkvia-guided-tour-active-${accountId.value}`
);
const seenStorageKey = computed(
  () => `talkvia-guided-tour-seen-${accountId.value}`
);

const inboxList = computed(() => inboxes.value || []);
const firstInbox = computed(() => inboxList.value[0]);
const channelRoute = subPage =>
  accountScopedRoute('settings_inboxes_page_channel', {
    sub_page: subPage,
  });

const steps = computed(() => [
  {
    title: 'Conecte canais oficiais',
    description:
      'Ative WhatsApp Oficial, Instagram e Messenger para centralizar o atendimento.',
    route: channelRoute('whatsapp'),
    routeNames: ['settings_inbox_show', 'settings_inboxes_page_channel'],
  },
  {
    title: 'Cadastre ou importe contatos',
    description:
      'Use esta área para criar contatos, revisar dados e preparar a base antes das mensagens e campanhas.',
    route: accountScopedRoute('contacts_dashboard_index', {}, { page: 1 }),
    routeNames: ['contacts_dashboard_index', 'contacts_imports'],
  },
  {
    title: 'Envie a primeira mensagem',
    description:
      'Aqui ficam as conversas. Use Nova conversa para testar o atendimento e validar sua conexão.',
    route: firstInbox.value
      ? accountScopedRoute('inbox_dashboard', { inbox_id: firstInbox.value.id })
      : accountScopedRoute('home'),
    routeNames: ['home', 'inbox_dashboard', 'inbox_conversation'],
  },
  {
    title: 'Prepare templates WhatsApp Oficial',
    description:
      'Templates aprovados pela Meta permitem iniciar conversas e campanhas com mensagens padronizadas.',
    route: accountScopedRoute('campaigns_whatsapp_templates'),
    routeNames: ['campaigns_whatsapp_templates'],
  },
  {
    title: 'Ative Instagram e Messenger',
    description:
      'Depois do WhatsApp Oficial, conecte Instagram e Messenger para cobrir os canais sociais.',
    route: channelRoute('instagram'),
    routeNames: ['settings_inboxes_page_channel', 'settings_inbox_show'],
  },
  {
    title: 'Crie sua primeira Agente de IA',
    description:
      'Na Biblioteca de IA, siga o tutorial para escolher um modelo, salvar o prompt e preparar a agente para o WhatsApp.',
    route: accountScopedRoute('talkvia_openai'),
    routeNames: ['talkvia_openai'],
  },
]);

const isOpen = computed(() => Number.isInteger(activeStep.value));
const currentStep = computed(() => steps.value[activeStep.value] || null);
const isLastStep = computed(() => activeStep.value === steps.value.length - 1);
const progressText = computed(
  () => `${copy.progressLabel} ${activeStep.value + 1} de ${steps.value.length}`
);

const persistStep = step => {
  localStorage.setItem(storageKey.value, JSON.stringify({ step }));
};

const clearTour = () => {
  activeStep.value = null;
  localStorage.removeItem(storageKey.value);
  localStorage.setItem(seenStorageKey.value, 'true');
};

const readStoredStep = () => {
  const rawValue = localStorage.getItem(storageKey.value);
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue);
    return Number.isInteger(parsed.step) ? parsed.step : null;
  } catch {
    return null;
  }
};

const isCurrentRouteForStep = step => {
  if (!step) return false;
  return step.routeNames.includes(route.name);
};

const updateSpotlight = ({ scroll = false } = {}) => {
  if (!isOpen.value) return;

  const element = document.querySelector('[data-tour-global-target]');
  if (!element) return;

  if (scroll) {
    element.scrollIntoView({
      block: 'center',
      inline: 'center',
      behavior: 'smooth',
    });
  }

  window.setTimeout(() => {
    const rect = element.getBoundingClientRect();
    const margin = 8;
    const tooltipWidth = Math.min(380, window.innerWidth - 32);
    const maxTooltipTop = Math.max(window.innerHeight - 280, 16);
    const leftCandidate =
      rect.right + tooltipWidth + 24 < window.innerWidth
        ? rect.right + 16
        : rect.left + 24;
    const topCandidate =
      rect.right + tooltipWidth + 24 < window.innerWidth
        ? rect.top + 24
        : rect.top + 24;

    spotlightStyle.value = {
      top: `${Math.max(rect.top - margin, 12)}px`,
      left: `${Math.max(rect.left - margin, 12)}px`,
      width: `${Math.min(rect.width + margin * 2, window.innerWidth - 24)}px`,
      height: `${Math.min(rect.height + margin * 2, window.innerHeight - 24)}px`,
    };
    tooltipStyle.value = {
      top: `${Math.min(Math.max(topCandidate, 16), maxTooltipTop)}px`,
      left: `${Math.min(Math.max(leftCandidate, 16), window.innerWidth - tooltipWidth - 16)}px`,
      width: `${tooltipWidth}px`,
    };
  }, 260);
};

const goToStep = async stepIndex => {
  if (stepIndex < 0 || stepIndex >= steps.value.length) {
    clearTour();
    return;
  }

  activeStep.value = stepIndex;
  persistStep(stepIndex);

  const step = steps.value[stepIndex];
  if (!isCurrentRouteForStep(step)) {
    await router.push(step.route);
  }
  nextTick(() => updateSpotlight({ scroll: true }));
};

const loadTourFromStorage = () => {
  const storedStep = readStoredStep();
  if (!Number.isInteger(storedStep)) return;

  activeStep.value = storedStep;
  const step = steps.value[storedStep];
  if (step && !isCurrentRouteForStep(step)) {
    router.push(step.route);
    return;
  }
  nextTick(() => updateSpotlight({ scroll: true }));
};

const nextStep = () => {
  if (isLastStep.value) {
    clearTour();
    return;
  }
  goToStep(activeStep.value + 1);
};

const previousStep = () => {
  if (activeStep.value <= 0) return;
  goToStep(activeStep.value - 1);
};

const handleKeydown = event => {
  if (!isOpen.value) return;
  if (event.key === 'Escape') clearTour();
  if (event.key === 'ArrowRight') nextStep();
  if (event.key === 'ArrowLeft') previousStep();
};

onMounted(() => {
  store.dispatch('inboxes/get');
  window.addEventListener('resize', updateSpotlight);
  window.addEventListener('keydown', handleKeydown);
  nextTick(loadTourFromStorage);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateSpotlight);
  window.removeEventListener('keydown', handleKeydown);
});

watch(
  () => route.fullPath,
  () => {
    if (isOpen.value) nextTick(() => updateSpotlight({ scroll: true }));
    else loadTourFromStorage();
  }
);
</script>

<template>
  <Teleport to="body">
    <div v-if="isOpen && currentStep" class="fixed inset-0 z-[9999]">
      <div class="absolute inset-0 bg-slate-950/70" />
      <div
        class="pointer-events-none fixed rounded-xl border-2 border-n-brand bg-white/5 shadow-[0_0_0_9999px_rgba(2,6,23,0.68),0_0_0_6px_rgba(59,130,246,0.18)] transition-all duration-200"
        :style="spotlightStyle"
      />
      <section
        class="fixed rounded-xl border border-n-weak bg-n-solid-1 p-4 text-n-slate-12 shadow-2xl transition-all duration-200"
        :style="tooltipStyle"
      >
        <div class="mb-3 flex items-center justify-between gap-3">
          <span class="text-xs font-semibold uppercase text-n-slate-10">
            {{ progressText }}
          </span>
          <button
            type="button"
            :title="copy.skip"
            class="grid size-7 place-content-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="clearTour"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
        <h2 class="text-lg font-semibold">{{ currentStep.title }}</h2>
        <p class="mt-2 text-sm leading-6 text-n-slate-11">
          {{ currentStep.description }}
        </p>
        <div class="mt-4 flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="activeStep === 0"
            @click="previousStep"
          >
            <span class="i-lucide-arrow-left size-4" />
            {{ copy.previous }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95"
            @click="nextStep"
          >
            {{ isLastStep ? copy.finish : copy.next }}
            <span class="i-lucide-arrow-right size-4" />
          </button>
        </div>
      </section>
    </div>
  </Teleport>
</template>
